import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Logo } from "../components/Logo";
import { EMAIL_DISPLAY, PHONE_DISPLAY, TEL_LINK } from "../lib/site";
import { useLang } from "../lib/i18n";

const CONTENT = {
  nl: {
    updated: "Laatst bijgewerkt: september 2026",
    back: "Terug naar de site",
    heading: "Privacyverklaring",
    intro:
      "4thscenery hecht veel waarde aan de bescherming van je persoonsgegevens. In deze privacyverklaring lees je welke gegevens we verzamelen via deze website, waarom we dat doen, hoe lang we ze bewaren en welke rechten je hebt onder de Algemene Verordening Gegevensbescherming (AVG).",
    emailUs: "e-mail ons",
    sections: [
      {
        title: "1. Wie zijn wij?",
        blocks: [
          { type: "p", text: `Deze website wordt beheerd door 4thscenery (KVK 95131108), een marketingbureau in Nederland. Voor vragen over deze verklaring of je gegevens kun je ons bereiken via ${EMAIL_DISPLAY} of bel ${PHONE_DISPLAY}.` },
        ],
      },
      {
        title: "2. Welke gegevens verzamelen wij?",
        blocks: [
          { type: "p", text: "Via deze website verzamelen we alleen gegevens die je zelf actief aan ons geeft:" },
          {
            type: "ul",
            items: [
              "Contactformulier: naam, telefoonnummer, e-mailadres en je bericht.",
              "AI-chatbot: de inhoud van het gesprek dat je met onze chatbot voert. De bot kan je bij concrete interesse om je naam en e-mailadres vragen; die worden dan als lead opgeslagen. De chat gebruikt een anonieme sessie-code die alleen in je eigen browser wordt bewaard.",
              "Online afspraak plannen: bij het boeken van een gesprek via de planner (Cal.com) deel je je naam, e-mailadres en het gekozen tijdstip.",
            ],
          },
          { type: "p", text: "We verzamelen geen bijzondere persoonsgegevens en de website is niet bedoeld voor personen jonger dan 16 jaar." },
        ],
      },
      {
        title: "3. Waarvoor gebruiken wij je gegevens?",
        blocks: [
          {
            type: "ul",
            items: [
              "Reageren op je aanvraag of bericht en contact met je opnemen.",
              "Het opvolgen van leads die via de chatbot of het formulier binnenkomen.",
              "Het inplannen en voorbereiden van (kennismakings)gesprekken.",
              "Het verbeteren van onze website en dienstverlening.",
            ],
          },
          { type: "p", text: "De grondslag hiervoor is je toestemming (je deelt de gegevens zelf), de voorbereiding van een overeenkomst, en ons gerechtvaardigd belang om onze dienstverlening te kunnen uitvoeren. We verkopen je gegevens nooit aan derden." },
        ],
      },
      {
        title: "4. Met welke partijen delen wij gegevens?",
        blocks: [
          { type: "p", text: "Voor het laten werken van deze website maken we gebruik van betrouwbare dienstverleners:" },
          {
            type: "ul",
            items: [
              "Hosting & database: de website en de opgeslagen gegevens draaien op beveiligde cloudinfrastructuur (Emergent, met een MongoDB-database).",
              "E-mailverzending: notificaties van formulieren en leads worden verstuurd via Resend.",
              "Agenda & boekingen: gespreksaanvragen lopen via Cal.com; daarvoor geldt ook het privacybeleid van Cal.com.",
              "AI-chatbot: gespreksvragen worden verwerkt door een AI-model (OpenAI, via Emergent) om een antwoord te kunnen geven.",
            ],
          },
          { type: "p", text: "Deze partijen verwerken gegevens uitsluitend in onze opdracht en mogen ze niet voor eigen doeleinden gebruiken." },
        ],
      },
      {
        title: "5. Cookies en lokale opslag",
        blocks: [
          { type: "p", text: "Deze website gebruikt geen tracking- of advertentiecookies. Er wordt alleen functionele opslag gebruikt die nodig is voor het functioneren van de site: een anonieme sessie-code in de lokale opslag van je browser voor de chatbot, en een functionele inlog-cookie voor het afgeschermde beheerdersgedeelte. Hiervoor is geen toestemming vereist." },
        ],
      },
      {
        title: "6. Hoe lang bewaren wij je gegevens?",
        blocks: [
          { type: "p", text: "We bewaren je gegevens niet langer dan nodig is voor het doel waarvoor je ze hebt gegeven. Aanvragen, chatgesprekken en leads bewaren we zolang als nodig is voor de opvolging van je aanvraag en onze dienstverlening. Wil je dat we je gegevens eerder verwijderen? Stuur ons een e-mail en we regelen het." },
        ],
      },
      {
        title: "7. Beveiliging",
        blocks: [
          { type: "p", text: "We nemen passende technische en organisatorische maatregelen om je gegevens te beveiligen, waaronder versleutelde verbindingen (HTTPS), een afgeschermde beheeromgeving en beperkte toegang tot opgeslagen gegevens." },
        ],
      },
      {
        title: "8. Jouw rechten",
        blocks: [
          { type: "p", text: "Je hebt onder de AVG de volgende rechten:" },
          {
            type: "ul",
            items: [
              "Inzage: opvragen welke gegevens we van je hebben.",
              "Rectificatie: onjuiste gegevens laten corrigeren.",
              "Verwijdering: je gegevens laten wissen (\"recht op vergetelheid\").",
              "Beperking, overdraagbaarheid en bezwaar tegen verwerking.",
              "Eerder gegeven toestemming te allen tijde intrekken.",
            ],
          },
          { type: "p", text: `Stuur hiervoor een e-mail naar ${EMAIL_DISPLAY}; we reageren binnen 30 dagen. Je hebt ook het recht een klacht in te dienen bij de Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl).` },
        ],
      },
      {
        title: "9. Wijzigingen",
        blocks: [
          { type: "p", text: "We kunnen deze privacyverklaring aanpassen als onze website of dienstverlening verandert. De meest actuele versie staat altijd op deze pagina." },
        ],
      },
    ],
  },
  en: {
    updated: "Last updated: September 2026",
    back: "Back to the site",
    heading: "Privacy Statement",
    intro:
      "4thscenery values the protection of your personal data. In this privacy statement you can read which data we collect through this website, why we do so, how long we keep it and which rights you have under the General Data Protection Regulation (GDPR).",
    emailUs: "email us",
    sections: [
      {
        title: "1. Who are we?",
        blocks: [
          { type: "p", text: `This website is operated by 4thscenery (Dutch Chamber of Commerce no. 95131108), a marketing agency based in the Netherlands. For questions about this statement or your data, contact us at ${EMAIL_DISPLAY} or call ${PHONE_DISPLAY}.` },
        ],
      },
      {
        title: "2. Which data do we collect?",
        blocks: [
          { type: "p", text: "Through this website we only collect data that you actively provide to us:" },
          {
            type: "ul",
            items: [
              "Contact form: your name, phone number, email address and your message.",
              "AI chatbot: the content of the conversation you have with our chatbot. When you show concrete interest, the bot may ask for your name and email address; those are then stored as a lead. The chat uses an anonymous session code that is only stored in your own browser.",
              "Booking a call online: when booking a call through the planner (Cal.com) you share your name, email address and the selected time slot.",
            ],
          },
          { type: "p", text: "We do not collect special categories of personal data and the website is not intended for persons under the age of 16." },
        ],
      },
      {
        title: "3. What do we use your data for?",
        blocks: [
          {
            type: "ul",
            items: [
              "Responding to your enquiry or message and getting in touch with you.",
              "Following up on leads received through the chatbot or the form.",
              "Scheduling and preparing (introductory) calls.",
              "Improving our website and services.",
            ],
          },
          { type: "p", text: "The legal basis for this is your consent (you provide the data yourself), the preparation of an agreement, and our legitimate interest in being able to deliver our services. We never sell your data to third parties." },
        ],
      },
      {
        title: "4. Which parties do we share data with?",
        blocks: [
          { type: "p", text: "To operate this website we use trusted service providers:" },
          {
            type: "ul",
            items: [
              "Hosting & database: the website and stored data run on secured cloud infrastructure (Emergent, with a MongoDB database).",
              "Email delivery: form and lead notifications are sent via Resend.",
              "Scheduling & bookings: call requests run via Cal.com; Cal.com's own privacy policy applies there as well.",
              "AI chatbot: chat questions are processed by an AI model (OpenAI, via Emergent) in order to generate a reply.",
            ],
          },
          { type: "p", text: "These parties process data solely on our behalf and may not use it for their own purposes." },
        ],
      },
      {
        title: "5. Cookies and local storage",
        blocks: [
          { type: "p", text: "This website does not use tracking or advertising cookies. Only functional storage that is necessary for the site to work is used: an anonymous session code in your browser's local storage for the chatbot, and a functional login cookie for the secured admin area. No consent is required for these." },
        ],
      },
      {
        title: "6. How long do we keep your data?",
        blocks: [
          { type: "p", text: "We do not keep your data longer than necessary for the purpose for which you provided it. Enquiries, chat conversations and leads are kept as long as needed to follow up on your enquiry and deliver our services. Want us to delete your data sooner? Send us an email and we will take care of it." },
        ],
      },
      {
        title: "7. Security",
        blocks: [
          { type: "p", text: "We take appropriate technical and organisational measures to protect your data, including encrypted connections (HTTPS), a secured admin environment and restricted access to stored data." },
        ],
      },
      {
        title: "8. Your rights",
        blocks: [
          { type: "p", text: "Under the GDPR you have the following rights:" },
          {
            type: "ul",
            items: [
              "Access: request which data we hold about you.",
              "Rectification: have incorrect data corrected.",
              "Erasure: have your data deleted (\"right to be forgotten\").",
              "Restriction, data portability and objection to processing.",
              "Withdraw previously given consent at any time.",
            ],
          },
          { type: "p", text: `To exercise these rights, email ${EMAIL_DISPLAY}; we respond within 30 days. You also have the right to file a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens, autoriteitpersoonsgegevens.nl).` },
        ],
      },
      {
        title: "9. Changes",
        blocks: [
          { type: "p", text: "We may update this privacy statement when our website or services change. The most current version is always available on this page." },
        ],
      },
    ],
  },
};

const Section = ({ title, children, testid }) => (
  <section className="mt-10" data-testid={testid}>
    <h2 className="font-archivo text-xl font-extrabold tracking-tight text-cream">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-tan sm:text-base">{children}</div>
  </section>
);

const Block = ({ block }) =>
  block.type === "ul" ? (
    <ul className="list-disc space-y-2 pl-5">
      {block.items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  ) : (
    <p>{block.text}</p>
  );

export default function Privacy() {
  const { lang, setLang } = useLang();
  const c = CONTENT[lang];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-ink" data-testid="privacy-page">
      <header className="border-b border-white/10 bg-[#222222]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1" data-testid="privacy-lang-toggle">
              {["nl", "en"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  data-testid={`privacy-lang-${code}`}
                  className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    lang === code ? "bg-amber font-bold text-[#181510]" : "text-white/50 hover:text-white"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
            <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-amber" data-testid="privacy-back-link">
              <ArrowLeft size={13} />
              {c.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-14">
        <div className="flex items-center gap-3">
          <ShieldCheck size={22} className="text-amberdeep" />
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-faint">{c.updated}</p>
        </div>
        <h1 className="mt-4 font-archivo text-4xl font-black tracking-tight text-cream" data-testid="privacy-heading">
          {c.heading}
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-tan sm:text-base" data-testid="privacy-intro">
          {c.intro}
        </p>

        {c.sections.map((s, i) => (
          <Section key={i} title={s.title} testid={`privacy-section-${i}`}>
            {s.blocks.map((b, j) => (
              <Block key={j} block={b} />
            ))}
          </Section>
        ))}
      </main>

      <footer className="border-t border-edge/40 py-8">
        <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-faint" data-testid="privacy-footer">
          © 2026 4thscenery · Beyond The Ordinary
        </p>
      </footer>
    </div>
  );
}
